// Integration search box state: the term, the last result set, and whether a
// search has run at all (which is what tells the empty state apart from "not
// searched yet").

import { useCallback, useMemo, useRef, useState } from 'react';
import noop from 'lodash/noop';
import { t } from '@lingui/macro';
import { useNotifications } from '@decisiv/ui-components';

import IntegrationsApi from '../../../api/integrations';

export default function useIntegrationSearch() {
  const [term, setTerm] = useState('');
  const [results, setResults] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  // Identifies the request whose response is still wanted. Clearing the box
  // abandons the one in flight, which would otherwise land afterwards and show
  // results for a term the user already erased.
  const currentRequest = useRef(0);
  const { notify } = useNotifications();

  const changeTerm = useCallback((value) => {
    setTerm(value);
    if (value === '') {
      currentRequest.current += 1;
      setResults([]);
      setHasSearched(false);
      setIsSearching(false);
    }
  }, []);

  const search = useCallback(async () => {
    if (!term.trim()) return;

    currentRequest.current += 1;
    const requestId = currentRequest.current;

    setIsSearching(true);
    const { data } = await IntegrationsApi.fetchIntegrations({
      nameSrm: term,
    });

    if (currentRequest.current !== requestId) return;

    setIsSearching(false);

    // The API module swallows the error and returns no `data`, so an outright
    // failure is indistinguishable from zero results unless it is caught here —
    // otherwise a 500 reads to the user as "this integration does not exist".
    if (!data) {
      setResults([]);
      setHasSearched(false);
      notify(
        {
          intent: 'warning',
          title: t`Failed to search integrations`,
          onClose: noop,
        },
        t`Please try again.`,
      );
      return;
    }

    setResults(data);
    setHasSearched(true);
  }, [term, notify]);

  // Memoized so the picker, which takes the whole result, can stay memoized too.
  return useMemo(
    () => ({ term, results, hasSearched, isSearching, changeTerm, search }),
    [term, results, hasSearched, isSearching, changeTerm, search],
  );
}
